import { useState, useEffect, useRef, useMemo } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { BookOpen, Check, ChevronDown, ChevronUp, Copy, Download, Loader2, Search } from 'lucide-react';
import { buildCsv, downloadCsv, gradeLabel, isoDate, todayStamp } from '@/lib/exportCsv';
import { useToast } from '@/hooks/use-toast';
import { formatDisplayName } from '@/lib/pupils';
import { normaliseGrade } from '@/lib/progress';
import {
  acceptAssessment,
  overrideAssessment,
  WRITING_TYPE_LABELS,
  type AssessmentGrade,
} from '@/lib/assessment';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import AssessmentCard, {
  type RowState,
  type ChatMessage,
} from '@/components/AssessmentCard';
import { MarkdownText } from '@/components/MarkdownText';
import GradeBadge from '@/components/GradeBadge';
import { useStructuredAssessments, useDeleteAssessment } from '@/hooks/useAssessments';
import { useSchoolFilters } from '@/hooks/useSchoolFilters';

// ---- Types ----------------------------------------------------------------

interface SavedAssessment {
  id: string;
  child_name: string;
  year_group: string;
  grade: string;
  assessment_text: string;
  created_at: string;
}

// ---- Component ------------------------------------------------------------

export default function Assessments() {
  const { user, profile, hasRole } = useAuth();
  const { toast } = useToast();
  const isSchoolWide = hasRole('school_admin') || hasRole('slt');
  const canManageAssessments = hasRole('school_admin');

  // ---- Shared assessment data (React Query) --------------------------------
  const {
    data: structuredAssessments = [],
    isLoading: structuredLoading,
  } = useStructuredAssessments(user?.id, isSchoolWide);

  const deleteMutation = useDeleteAssessment(user?.id);
  const { data: schoolFilters } = useSchoolFilters(profile?.school_id);

  // ---- Local UI state ------------------------------------------------------
  const [rowStates, setRowStates] = useState<Record<string, RowState>>({});
  const [assessments, setAssessments] = useState<SavedAssessment[]>([]);
  const [savedLoading, setSavedLoading] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [copiedSaved, setCopiedSaved] = useState<string | null>(null);
  const chatBottomRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterGrade, setFilterGrade] = useState<string>('all');
  const [filterPeriod, setFilterPeriod] = useState<string>('all');
  const [filterKeyStage, setFilterKeyStage] = useState<string>('all');
  const [filterYearGroup, setFilterYearGroup] = useState<string>('all');
  const [filterClass, setFilterClass] = useState<string>('all');
  const INITIAL_VISIBLE = 10;
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);

  // ---- Sync rowStates when query data arrives (only add new entries) --------
  useEffect(() => {
    setRowStates((prev) => {
      const next = { ...prev };
      for (const row of structuredAssessments) {
        if (!next[row.id]) {
          next[row.id] = {
            status: row.status,
            final_grade: row.final_grade,
            ai_grade: row.ai_grade,
            gradeAction: 'none',
            overrideGrade: null,
            isActioning: false,
            chatOpen: false,
            chatMessages: [],
            chatInput: '',
            chatLoading: false,
            imageOpen: false,
            fluencyOpen: false,
            extractedTextOpen: false,
            deleting: false,
          };
        }
      }
      return next;
    });
  }, [structuredAssessments]);

  // ---- Fetch saved (ad-hoc) assessments ------------------------------------
  useEffect(() => {
    if (!user) return;
    const savedQuery = isSchoolWide
      ? supabase.from('saved_assessments').select('*').order('created_at', { ascending: false })
      : supabase.from('saved_assessments').select('*').eq('assessed_by', user.id).order('created_at', { ascending: false });
    savedQuery
      .then(({ data, error }) => {
        if (error) {
          toast({ title: 'Failed to load saved assessments', description: error.message, variant: 'destructive' });
        } else if (data) {
          setAssessments(data);
        }
        setSavedLoading(false);
      });
  }, [user, toast, isSchoolWide]);

  const loading = structuredLoading || savedLoading;

  // ---- Handlers ------------------------------------------------------------

  const toggleExpanded = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleCopySaved = (id: string, text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedSaved(id);
    setTimeout(() => setCopiedSaved((prev) => (prev === id ? null : prev)), 2000);
  };

  const handleDelete = (rowId: string) => {
    // Start CSS fade-out, then fire optimistic mutation after animation
    setRowStates((prev) => ({
      ...prev,
      [rowId]: { ...prev[rowId], deleting: true },
    }));
    setTimeout(() => {
      setRowStates((prev) => {
        const next = { ...prev };
        delete next[rowId];
        return next;
      });
      deleteMutation.mutate(rowId, {
        onError: (err) => {
          toast({
            title: 'Error deleting assessment',
            description: err.message,
            variant: 'destructive',
          });
        },
      });
    }, 200);
  };

  const handleRowAccept = async (rowId: string) => {
    const row = rowStates[rowId];
    if (!row) return;
    setRowStates((prev) => ({
      ...prev,
      [rowId]: { ...prev[rowId], gradeAction: 'accepting', isActioning: true },
    }));
    const error = await acceptAssessment(rowId, row.ai_grade);
    if (error) {
      toast({ title: 'Error accepting grade', description: error.message, variant: 'destructive' });
      setRowStates((prev) => ({ ...prev, [rowId]: { ...prev[rowId], isActioning: false } }));
    } else {
      setRowStates((prev) => ({
        ...prev,
        [rowId]: {
          ...prev[rowId],
          status: 'accepted',
          final_grade: row.ai_grade,
          gradeAction: 'accepted',
          isActioning: false,
        },
      }));
    }
  };

  const handleRowConfirmOverride = async (rowId: string) => {
    const row = rowStates[rowId];
    if (!row?.overrideGrade) return;
    setRowStates((prev) => ({ ...prev, [rowId]: { ...prev[rowId], isActioning: true } }));
    const error = await overrideAssessment(rowId, row.overrideGrade);
    if (error) {
      toast({ title: 'Error overriding grade', description: error.message, variant: 'destructive' });
      setRowStates((prev) => ({ ...prev, [rowId]: { ...prev[rowId], isActioning: false } }));
    } else {
      setRowStates((prev) => ({
        ...prev,
        [rowId]: {
          ...prev[rowId],
          status: 'modified',
          final_grade: row.overrideGrade,
          gradeAction: 'overridden',
          isActioning: false,
        },
      }));
    }
  };

  // Auto-scroll chat to bottom
  useEffect(() => {
    for (const id of Object.keys(rowStates)) {
      if (rowStates[id].chatOpen) {
        chatBottomRefs.current[id]?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Object.values(rowStates).map((rs) => rs.chatMessages.length).join(',')]);

  const handleToggleChat = async (rowId: string) => {
    const rs = rowStates[rowId];
    if (!rs) return;
    const opening = !rs.chatOpen;
    setRowStates((prev) => ({ ...prev, [rowId]: { ...prev[rowId], chatOpen: opening } }));
    if (opening && rs.chatMessages.length === 0) {
      const { data, error } = await supabase
        .from('assessment_chats')
        .select('id, role, content, created_at')
        .eq('assessment_id', rowId)
        .order('created_at', { ascending: true });
      if (!error && data) {
        setRowStates((prev) => ({
          ...prev,
          [rowId]: { ...prev[rowId], chatMessages: data as ChatMessage[] },
        }));
      }
    }
  };

  const handleToggleImage = (rowId: string) => {
    setRowStates((prev) => ({
      ...prev,
      [rowId]: { ...prev[rowId], imageOpen: !prev[rowId].imageOpen },
    }));
  };

  const handleSendChat = async (rowId: string) => {
    const rs = rowStates[rowId];
    if (!rs || !rs.chatInput.trim() || rs.chatLoading) return;
    const userMessage: ChatMessage = {
      id: `optimistic-${Date.now()}`,
      role: 'user',
      content: rs.chatInput.trim(),
      created_at: new Date().toISOString(),
    };
    setRowStates((prev) => ({
      ...prev,
      [rowId]: {
        ...prev[rowId],
        chatMessages: [...prev[rowId].chatMessages, userMessage],
        chatInput: '',
        chatLoading: true,
      },
    }));
    const { data, error } = await supabase.functions.invoke('assessment-chat', {
      body: { assessmentId: rowId, message: userMessage.content },
    });
    if (error || !data?.content) {
      toast({
        title: 'Chat error',
        description: error?.message ?? 'No response received. Please try again.',
        variant: 'destructive',
      });
      setRowStates((prev) => ({ ...prev, [rowId]: { ...prev[rowId], chatLoading: false } }));
      return;
    }
    setRowStates((prev) => ({
      ...prev,
      [rowId]: {
        ...prev[rowId],
        chatMessages: [
          ...prev[rowId].chatMessages,
          {
            id: `assistant-${Date.now()}`,
            role: 'assistant' as const,
            content: data.content,
            created_at: new Date().toISOString(),
          },
        ],
        chatLoading: false,
      },
    }));
  };

  // ---- CSV export ----------------------------------------------------------

  const handleExportCsv = () => {
    const headers = [
      'type', 'pupil_name', 'year_group', 'class_name', 'key_stage',
      'writing_type', 'assessment_date', 'ai_grade', 'final_grade', 'status',
      'assessment', 'extracted_text',
      'syntactic_maturity', 'textual_cohesion', 'lexical_precision', 'fluency_summary',
    ];

    const structuredRows = filteredStructured.map((a) => {
      const rs = rowStates[a.id];
      const pupil = a.writing_samples?.pupils;
      const name = pupil
        ? formatDisplayName(pupil.first_name, pupil.last_name, pupil.anonymisation)
        : '';
      const fs = a.fluency_score;
      return [
        'Structured',
        name,
        pupil?.classes?.year_groups?.name ?? '',
        pupil?.classes?.name ?? '',
        pupil?.classes?.year_groups?.key_stages?.name ?? '',
        WRITING_TYPE_LABELS[a.writing_samples?.writing_type ?? ''] ?? a.writing_samples?.writing_type ?? '',
        isoDate(a.created_at),
        gradeLabel(a.ai_grade),
        gradeLabel(rs?.final_grade ?? a.final_grade),
        rs?.status ?? a.status,
        a.justification ?? '',
        a.extracted_text ?? '',
        fs?.syntactic_maturity ?? '',
        fs?.textual_cohesion ?? '',
        fs?.lexical_precision ?? '',
        fs?.summary ?? '',
      ];
    });

    const savedRows = filteredSaved.map((a) => [
      'Saved', a.child_name, a.year_group, '', '', '',
      isoDate(a.created_at),
      gradeLabel(a.grade), gradeLabel(a.grade), '',
      a.assessment_text, '', '', '', '', '',
    ]);

    downloadCsv(
      `assessments_${todayStamp()}.csv`,
      buildCsv(headers, [...structuredRows, ...savedRows]),
    );
  };

  // ---- Filtering -----------------------------------------------------------

  const applyDateFilter = (dateStr: string): boolean => {
    if (filterPeriod === 'all') return true;
    const date = new Date(dateStr);
    const now = new Date();
    if (filterPeriod === '7' || filterPeriod === '30' || filterPeriod === '90') {
      const cutoff = new Date(now);
      cutoff.setDate(cutoff.getDate() - parseInt(filterPeriod));
      return date >= cutoff;
    }
    if (filterPeriod === 'this-term') {
      const m = now.getMonth() + 1;
      const y = now.getFullYear();
      const start = m >= 9 ? new Date(y, 8, 1) : m <= 3 ? new Date(y, 0, 1) : new Date(y, 3, 1);
      return date >= start;
    }
    if (filterPeriod === 'this-year') {
      const m = now.getMonth() + 1;
      const y = now.getFullYear();
      const start = new Date(m >= 9 ? y : y - 1, 8, 1);
      return date >= start;
    }
    return true;
  };

  const filteredStructured = useMemo(() => {
    return structuredAssessments.filter((a) => {
      const rs = rowStates[a.id];
      const pupil = a.writing_samples?.pupils;
      const displayName = pupil
        ? formatDisplayName(pupil.first_name, pupil.last_name, pupil.anonymisation).toLowerCase()
        : '';
      if (searchQuery.trim() && !displayName.includes(searchQuery.toLowerCase())) return false;
      if (filterGrade !== 'all') {
        const grade = rs?.final_grade ?? rs?.ai_grade;
        if (grade !== filterGrade) return false;
      }
      if (!applyDateFilter(a.created_at)) return false;
      if (filterKeyStage !== 'all') {
        const ks = pupil?.classes?.year_groups?.key_stages?.name ?? '';
        if (ks !== filterKeyStage) return false;
      }
      if (filterYearGroup !== 'all') {
        const yg = pupil?.classes?.year_groups?.name ?? '';
        if (yg !== filterYearGroup) return false;
      }
      if (filterClass !== 'all') {
        const cl = pupil?.classes?.name ?? '';
        if (cl !== filterClass) return false;
      }
      return true;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [structuredAssessments, rowStates, searchQuery, filterGrade, filterPeriod, filterKeyStage, filterYearGroup, filterClass]);

  const visibleStructured = filteredStructured.slice(0, visibleCount);
  const hasMoreStructured = filteredStructured.length > visibleCount;

  const filteredSaved = useMemo(() => {
    return assessments.filter((a) => {
      if (searchQuery.trim() && !a.child_name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      if (filterGrade !== 'all' && normaliseGrade(a.grade) !== filterGrade) return false;
      if (!applyDateFilter(a.created_at)) return false;
      if (filterYearGroup !== 'all' && a.year_group !== filterYearGroup) return false;
      return true;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessments, searchQuery, filterGrade, filterPeriod, filterYearGroup]);

  return (
    <DashboardLayout>
      <div className="p-6 max-w-2xl mx-auto space-y-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <h1 className="text-2xl font-bold">Assessments</h1>
          {!loading && (filteredStructured.length > 0 || filteredSaved.length > 0) && (
            <Button variant="outline" size="sm" onClick={handleExportCsv}>
              <Download className="h-4 w-4 mr-1.5" />
              Export CSV
            </Button>
          )}
        </div>

        {/* Filter bar */}
        {!loading && (structuredAssessments.length > 0 || assessments.length > 0) && (
          <div className="flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-40">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by pupil name..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setVisibleCount(INITIAL_VISIBLE); }}
                className="pl-9"
              />
            </div>
            <Select value={filterGrade} onValueChange={(v) => { setFilterGrade(v); setVisibleCount(INITIAL_VISIBLE); }}>
              <SelectTrigger className="w-44">
                <SelectValue placeholder="All grades" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All grades</SelectItem>
                <SelectItem value="working_towards">Working Towards</SelectItem>
                <SelectItem value="expected">Expected</SelectItem>
                <SelectItem value="greater_depth">Greater Depth</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterPeriod} onValueChange={(v) => { setFilterPeriod(v); setVisibleCount(INITIAL_VISIBLE); }}>
              <SelectTrigger className="w-44">
                <SelectValue placeholder="All time" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All time</SelectItem>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="this-term">This term</SelectItem>
                <SelectItem value="this-year">This academic year</SelectItem>
              </SelectContent>
            </Select>
            {(schoolFilters?.keyStages?.length ?? 0) > 0 && (
              <Select value={filterKeyStage} onValueChange={(v) => { setFilterKeyStage(v); setVisibleCount(INITIAL_VISIBLE); }}>
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="All key stages" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All key stages</SelectItem>
                  {schoolFilters!.keyStages.map((ks) => (
                    <SelectItem key={ks.id} value={ks.name}>{ks.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {(schoolFilters?.yearGroups?.length ?? 0) > 0 && (
              <Select value={filterYearGroup} onValueChange={(v) => { setFilterYearGroup(v); setVisibleCount(INITIAL_VISIBLE); }}>
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="All year groups" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All year groups</SelectItem>
                  {schoolFilters!.yearGroups.map((yg) => (
                    <SelectItem key={yg.id} value={yg.name}>{yg.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {(schoolFilters?.classes?.length ?? 0) > 0 && (
              <Select value={filterClass} onValueChange={(v) => { setFilterClass(v); setVisibleCount(INITIAL_VISIBLE); }}>
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="All classes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All classes</SelectItem>
                  {schoolFilters!.classes.map((cl) => (
                    <SelectItem key={cl.id} value={cl.name}>{cl.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        )}

        {/* Structured assessments */}
        {structuredLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : structuredAssessments.length > 0 ? (
          <div className="space-y-3">
            <h2 className="text-xl font-bold">Recent Structured Assessments</h2>
            {visibleStructured.map((a) => {
              const rs = rowStates[a.id];
              if (!rs) return null;
              return (
                <AssessmentCard
                  key={a.id}
                  assessment={a}
                  rowState={rs}
                  canManage={canManageAssessments}
                  onAccept={() => handleRowAccept(a.id)}
                  onStartOverride={() =>
                    setRowStates((prev) => ({
                      ...prev,
                      [a.id]: { ...prev[a.id], gradeAction: 'overriding' },
                    }))
                  }
                  onConfirmOverride={() => handleRowConfirmOverride(a.id)}
                  onCancelOverride={() =>
                    setRowStates((prev) => ({
                      ...prev,
                      [a.id]: { ...prev[a.id], gradeAction: 'none', overrideGrade: null },
                    }))
                  }
                  onOverrideGradeChange={(grade) =>
                    setRowStates((prev) => ({
                      ...prev,
                      [a.id]: { ...prev[a.id], overrideGrade: grade },
                    }))
                  }
                  onToggleChat={() => handleToggleChat(a.id)}
                  onSendChat={() => handleSendChat(a.id)}
                  onChatInputChange={(value) =>
                    setRowStates((prev) => ({
                      ...prev,
                      [a.id]: { ...prev[a.id], chatInput: value },
                    }))
                  }
                  onToggleImage={() => handleToggleImage(a.id)}
                  onFluencyOpenChange={(open) =>
                    setRowStates((prev) => ({ ...prev, [a.id]: { ...prev[a.id], fluencyOpen: open } }))
                  }
                  onToggleExtractedText={() =>
                    setRowStates((prev) => ({
                      ...prev,
                      [a.id]: { ...prev[a.id], extractedTextOpen: !prev[a.id].extractedTextOpen },
                    }))
                  }
                  onDelete={() => handleDelete(a.id)}
                  chatBottomRef={(el) => { chatBottomRefs.current[a.id] = el; }}
                />
              );
            })}
            {hasMoreStructured && (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setVisibleCount((n) => n + 10)}
              >
                Show older ({filteredStructured.length - visibleCount} more)
              </Button>
            )}
            {filteredStructured.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">
                No assessments match your filters.
              </p>
            )}
          </div>
        ) : null}

        {/* Saved assessments */}
        <h2 className="text-xl font-bold">Saved Assessments</h2>

        {loading ? (
          <p className="text-muted-foreground text-sm">Loading...</p>
        ) : assessments.length === 0 && structuredAssessments.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              <BookOpen className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <p>No saved assessments yet.</p>
              <p className="text-sm mt-1">
                Run an assessment on the Upload page and click "Save Assessment".
              </p>
            </CardContent>
          </Card>
        ) : filteredSaved.length === 0 && assessments.length > 0 ? (
          <p className="text-sm text-muted-foreground">No saved assessments match your filters.</p>
        ) : filteredSaved.length === 0 ? null : (
          filteredSaved.map((a) => (
            <Card key={a.id}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center justify-between">
                  <span>{a.child_name}</span>
                  <GradeBadge grade={normaliseGrade(a.grade)} />
                </CardTitle>
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <span>{a.year_group}</span>
                  <span>{new Date(a.created_at).toLocaleDateString()}</span>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex-1 justify-between text-sm"
                    onClick={() => toggleExpanded(a.id)}
                  >
                    {expanded.has(a.id) ? 'Hide Assessment' : 'View Assessment'}
                    {expanded.has(a.id) ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleCopySaved(a.id, a.assessment_text)}
                    title="Copy assessment text"
                  >
                    {copiedSaved === a.id ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
                {expanded.has(a.id) && (
                  <MarkdownText variant="body" className="mt-2">
                    {a.assessment_text}
                  </MarkdownText>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </DashboardLayout>
  );
}
